import { useState, useEffect, useRef, useCallback } from "react";
import {
  Save,
  Download,
  Search,
  Loader2,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import ExportCartridgeModal from "../../modals/ExportCartridgeModal";
import { Standard } from "../../../types";
import {
  transformToCartridgeFormat,
  validateCartridgeData,
  CourseData,
} from "../../../utils/exportCartridge";
import {
  exportCartridgeFrontend,
  downloadCartridge,
} from "../../../utils/frontendExportCartridge";
import { ClassData, getClasses } from "../../../utils/getClasses";
import { editClassPlatform } from "../../../utils/editClass";
import { toast } from "react-toastify";
import supabase from "../../../utils/supabase";
import useUserStore from "../../../stores/user";

interface Platform {
  id: string;
  name: string;
  hours: number;
  type: "synchronous" | "asynchronous";
  description: string;
}

export default function SuperAdminCurriculumConfig() {
  const [selectedPlatform, setSelectedPlatform] = useState<string>("sync-90");
  const [selectedClass, setSelectedClass] = useState<string>("");
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [classes, setClasses] = useState<ClassData[]>([]);
  const [isLoadingClasses, setIsLoadingClasses] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [classError, setClassError] = useState<string | null>(null);
  const [districtName, setDistrictName] = useState<string | null>(null);
  const [schoolName, setSchoolName] = useState<string | null>(null);
  const [isLoadingClassInfo, setIsLoadingClassInfo] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalClasses, setTotalClasses] = useState(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [filteredClasses, setFilteredClasses] = useState<ClassData[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const user = useUserStore((state) => state.user);
  const profile = useUserStore((state) => state.profile);

  const CLASSES_PER_PAGE = 5;

  // Debounced search function
  const debouncedSearch = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return (searchValue: string) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(async () => {
          if (!searchValue.trim()) {
            // Reset to original classes if search is cleared
            setFilteredClasses(classes);
            return;
          }

          try {
            const { data: searchResults, error } = await supabase
              .from("classes")
              .select("*")
              .ilike("name", `%${searchValue.trim()}%`)
              .order("created_at", { ascending: false })
              .limit(50); // Limit search results

            if (error) {
              console.error("Error searching classes:", error);
              toast.error("Failed to search classes");
            } else {
              setFilteredClasses(searchResults || []);
            }
          } catch (error) {
            console.error("Error searching classes:", error);
            toast.error("Failed to search classes");
          }
        }, 500); // 500ms debounce delay
      };
    })(),
    [classes]
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isDropdownOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isDropdownOpen]);

  // Trigger debounced search when search term changes
  useEffect(() => {
    debouncedSearch(searchTerm);
  }, [searchTerm, debouncedSearch]);

  useEffect(() => {
    async function fetchClasses() {
      setIsLoadingClasses(true);
      setIsLoadingClassInfo(true);
      setCurrentPage(0);

      try {
        // Get total count first
        const { count } = await supabase
          .from("classes")
          .select("*", { count: "exact", head: true });

        setTotalClasses(count || 0);

        // Fetch first page
        const { data: classesData, error } = await supabase
          .from("classes")
          .select("*")
          .order("created_at", { ascending: false })
          .range(0, CLASSES_PER_PAGE - 1);

        if (error) {
          setClassError(error.message);
          setClasses([]);
        } else {
          setClasses(classesData || []);
          setFilteredClasses(classesData || []);
          setHasMore((classesData?.length || 0) === CLASSES_PER_PAGE);

          if (classesData && classesData.length > 0) {
            setSelectedClass(classesData[0].id);
            if (classesData[0].platform) {
              setSelectedPlatform(classesData[0].platform);
            }
            if (classesData[0].district_id) {
              await fetchDistrictName(classesData[0].district_id);
            }
            if (classesData[0].user_id) {
              await fetchSchoolName(classesData[0].user_id);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching classes:", error);
        setClassError("Failed to fetch classes");
        setClasses([]);
      } finally {
        setIsLoadingClasses(false);
        setIsLoadingClassInfo(false);
      }
    }

    if (profile?.role === "super_admin") {
      fetchClasses();
    }
  }, [profile]);

  const loadMoreClasses = async () => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    const nextPage = currentPage + 1;
    const from = nextPage * CLASSES_PER_PAGE;
    const to = from + CLASSES_PER_PAGE - 1;

    try {
      const { data: moreClasses, error } = await supabase
        .from("classes")
        .select("*")
        .order("created_at", { ascending: false })
        .range(from, to);

      if (error) {
        console.error("Error loading more classes:", error);
        toast.error("Failed to load more classes");
      } else {
        setClasses((prev) => [...prev, ...(moreClasses || [])]);
        setCurrentPage(nextPage);
        setHasMore((moreClasses?.length || 0) === CLASSES_PER_PAGE);
      }
    } catch (error) {
      console.error("Error loading more classes:", error);
      toast.error("Failed to load more classes");
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleClassSelect = async (classId: string) => {
    setSelectedClass(classId);
    setIsDropdownOpen(false);
    setSearchTerm("");
    setIsLoadingClassInfo(true);

    // Look for the class in both filteredClasses and classes arrays
    const classObj =
      filteredClasses.find((c) => c.id === classId) ||
      classes.find((c) => c.id === classId);

    // If the class is from search results and not in the main classes array, add it
    if (classObj && !classes.find((c) => c.id === classId)) {
      setClasses((prev) => [classObj, ...prev]);
    }

    if (classObj && classObj.platform) {
      setSelectedPlatform(classObj.platform);
    }

    if (classObj && classObj.district_id) {
      await fetchDistrictName(classObj.district_id);
    } else {
      setDistrictName(null);
    }

    if (classObj && classObj.user_id) {
      await fetchSchoolName(classObj.user_id);
    } else {
      setSchoolName(null);
    }

    setIsLoadingClassInfo(false);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
    if (!isDropdownOpen) {
      setSearchTerm("");
    }
  };

  const platforms: Platform[] = [
    {
      id: "90-hours",
      name: "Synchronous 90-Hour Platform",
      hours: 90,
      type: "synchronous",
      description:
        "Complete synchronous platform aligned with Oklahoma state requirements for high school graduation",
    },
    {
      id: "45-hours",
      name: "Asynchronous 45-Hour Platform",
      hours: 45,
      type: "asynchronous",
      description:
        "Flexible asynchronous learning platform for shorter course requirements",
    },
    {
      id: "30-hours",
      name: "Asynchronous 30-Hour Platform",
      hours: 30,
      type: "asynchronous",
      description:
        "Condensed asynchronous platform for basic financial literacy requirements",
    },
  ];

  const handlePlatformChange = (platformId: string) => {
    setSelectedPlatform(platformId);
  };

  const fetchDistrictName = async (districtId: string) => {
    try {
      const { data, error } = await supabase
        .from("districts")
        .select("name")
        .eq("id", districtId)
        .maybeSingle();

      if (error) {
        console.error("Error fetching district:", error);
        setDistrictName(null);
      } else {
        setDistrictName(data?.name || null);
      }
    } catch (error) {
      console.error("Error fetching district:", error);
      setDistrictName(null);
    }
  };

  const fetchSchoolName = async (teacherId: string) => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("school")
        .eq("id", teacherId)
        .maybeSingle();

      if (error) {
        console.error("Error fetching teacher profile:", error);
        setSchoolName(null);
        return;
      }

      if (data?.school) {
        const { data: schoolData, error: schoolError } = await supabase
          .from("schools")
          .select("name")
          .eq("id", data.school)
          .maybeSingle();

        if (schoolError) {
          console.error("Error fetching school:", schoolError);
          setSchoolName(null);
        } else {
          setSchoolName(schoolData?.name || null);
        }
      } else {
        setSchoolName(null);
      }
    } catch (error) {
      console.error("Error fetching school name:", error);
      setSchoolName(null);
    }
  };

  const handleSave = async () => {
    if (!selectedClass) {
      toast.error("No class selected.");
      return;
    }
    try {
      const { error } = await editClassPlatform(
        selectedClass,
        selectedPlatform
      );
      if (error) {
        toast.error("Error saving configuration");
        console.error("Error saving configuration:", error);
      } else {
        toast.success("Configuration saved successfully!");
      }
    } catch (error) {
      toast.error(error);
      console.error("Error saving configuration:", error);
    }
  };

  const handleExportCartridge = async (
    selectedStandards: Standard[],
    courseData: CourseData
  ) => {
    setIsExporting(true);
    try {
      const cartridgeData = transformToCartridgeFormat(
        selectedStandards,
        courseData
      );

      const validationErrors = validateCartridgeData(cartridgeData);
      if (validationErrors.length > 0) {
        console.error(`Validation errors:\n${validationErrors.join("\n")}`);
        toast.error(`Export validation failed: ${validationErrors.join(", ")}`);
        return;
      }

      const { blob, filename } = await exportCartridgeFrontend(cartridgeData);
      downloadCartridge(blob, filename);

      toast.success("Cartridge exported successfully!");
      setIsExportModalOpen(false);
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Export failed. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const selectedClassData =
    filteredClasses.find((c) => c.id === selectedClass) ||
    classes.find((c) => c.id === selectedClass);

  if (profile?.role !== "super_admin") {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Access Denied
          </h2>
          <p className="text-gray-600">
            Super admin privileges are required to access this feature.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="bg-white border-b shadow-sm p-6  flex justify-between gap-5 items-start lg:items-center flex-col lg:flex-row">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Curriculum Configuration
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Configure the curriculum settings for all classes across all schools
            and districts.
          </p>
        </div>
        <div className="space-x-2 flex gap-2">
          <button
            onClick={() => setIsExportModalOpen(true)}
            className="btn btn-small"
          >
            <Download className="w-5 h-5 mr-2" />
            Export Cartridge
          </button>
          <button onClick={handleSave} className="btn btn-small">
            <Save className="w-5 h-5 mr-2" />
            Save Configuration
          </button>
        </div>
      </div>
      <div className=" bg-gray-50 ">
        <div className="max-w-7xl mx-auto py-6 px-4">
          <div className="bg-white rounded-lg shadow">
            <div className="px-4 py-5 sm:p-6">
              {/* Class Selection with Searchable Dropdown */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Class
                </label>
                <div className="relative" ref={dropdownRef}>
                  {isLoadingClasses ? (
                    <div className="text-gray-500 text-sm flex items-center">
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Loading classes...
                    </div>
                  ) : classError ? (
                    <div className="text-red-500 text-sm">{classError}</div>
                  ) : classes.length === 0 ? (
                    <div className="text-gray-500 text-sm">
                      No classes found.
                    </div>
                  ) : (
                    <>
                      {/* Dropdown Button */}
                      <button
                        type="button"
                        onClick={toggleDropdown}
                        className="relative w-full bg-white border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      >
                        <span className="block truncate">
                          {selectedClassData
                            ? selectedClassData.name
                            : "Select a class"}
                        </span>
                        <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                          {isDropdownOpen ? (
                            <ChevronUp className="h-5 w-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-400" />
                          )}
                        </span>
                      </button>

                      {/* Dropdown Menu */}
                      {isDropdownOpen && (
                        <div className="absolute z-10 mt-2 w-full bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden">
                          {/* Search Input */}
                          <div className="p-4 bg-gray-50 border-b border-gray-200">
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                              <input
                                ref={searchInputRef}
                                type="text"
                                placeholder="Type to search classes..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                              />
                            </div>
                          </div>

                          {/* Classes List */}
                          <div className="max-h-64 overflow-y-auto">
                            {filteredClasses.length === 0 ? (
                              <div className="p-4 text-center text-gray-500">
                                <div className="text-sm">
                                  {searchTerm ? (
                                    <>
                                      <Search className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                                      <p>
                                        No classes found matching "{searchTerm}"
                                      </p>
                                    </>
                                  ) : (
                                    <>
                                      <div className="h-8 w-8 mx-auto mb-2 bg-gray-100 rounded-full flex items-center justify-center">
                                        <span className="text-gray-400 text-xs">
                                          📚
                                        </span>
                                      </div>
                                      <p>No classes available</p>
                                    </>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div className="py-2">
                                {filteredClasses.map((classItem) => (
                                  <button
                                    key={classItem.id}
                                    onClick={() =>
                                      handleClassSelect(classItem.id)
                                    }
                                    className={`w-full text-left px-4 py-3 hover:bg-gray-200 transition-colors duration-150 ${
                                      selectedClass === classItem.id
                                        ? "bg-gray-200"
                                        : ""
                                    }`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex-1">
                                        <div className="font-medium text-gray-900 text-sm">
                                          {classItem.name}
                                        </div>
                                      </div>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Load More Button */}
                          {hasMore && (
                            <div className="border-t border-gray-200 bg-gray-50">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  loadMoreClasses();
                                }}
                                disabled={isLoadingMore}
                                className="w-full px-4 py-3 text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                              >
                                {isLoadingMore ? (
                                  <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Loading more...
                                  </>
                                ) : (
                                  <>
                                    <span>Load More Classes</span>
                                    <ChevronDown className="h-4 w-4 ml-2" />
                                  </>
                                )}
                              </button>
                            </div>
                          )}

                          {/* Total Count */}
                          <div className="px-4 py-2 bg-gray-50 border-t border-gray-200">
                            <div className="text-xs text-gray-500 text-center">
                              <span className="font-medium">
                                {filteredClasses.length}
                              </span>{" "}
                              of{" "}
                              <span className="font-medium">
                                {totalClasses}
                              </span>{" "}
                              classes
                              {searchTerm && (
                                <span className="block mt-1">
                                  matching "{searchTerm}"
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Platform Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-4">
                  Platform Selection
                </label>
                <div className="space-y-4">
                  {platforms.map((platform) => (
                    <div
                      key={platform.id}
                      className={`relative rounded-lg border p-4 cursor-pointer transition-colors ${
                        selectedPlatform === platform.id
                          ? "border-indigo-600 bg-indigo-50"
                          : "border-gray-300 hover:border-indigo-400"
                      }`}
                      onClick={() => handlePlatformChange(platform.id)}
                    >
                      <div className="flex justify-between">
                        <div className="flex items-center">
                          <div className="text-sm">
                            <p className="font-medium text-gray-900">
                              {platform.name}
                            </p>
                            <p className="text-gray-500">
                              {platform.description}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              platform.type === "synchronous"
                                ? "bg-green-100 text-green-800"
                                : "bg-blue-100 text-blue-800"
                            }`}
                          >
                            {platform.type}
                          </span>
                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            {platform.hours} hours
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Standards Notice */}
              <div className="mt-8 bg-blue-50 p-4 rounded-md">
                <h3 className="text-sm font-medium text-gray-900">
                  Class Information
                </h3>
                {selectedClassData && !isLoadingClassInfo ? (
                  <>
                    <p className="mt-2 text-sm text-gray-500">
                      Class: {selectedClassData.name}
                    </p>
                    <p className="mt-1 text-sm text-gray-500">
                      {districtName
                        ? `District: ${districtName}`
                        : "District: Not available"}
                    </p>
                    {schoolName && (
                      <p className="mt-1 text-sm text-gray-500">
                        School: {schoolName}
                      </p>
                    )}
                  </>
                ) : (
                  <div className="mt-2 space-y-2">
                    <div className="animate-pulse bg-gray-300 h-4 w-32 rounded"></div>
                    <div className="animate-pulse bg-gray-300 h-4 w-40 rounded"></div>
                    <div className="animate-pulse bg-gray-300 h-4 w-36 rounded"></div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <ExportCartridgeModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onExport={handleExportCartridge}
        isLoading={isExporting}
      />
    </div>
  );
}
